import React, { type ReactNode, useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { useUCSuggestions } from '@atlassian/navigation-bff-data-provider';
import { ConditionalRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { useMenuControl } from '../../../context';
import {
	useSuggestionExcludeAtis,
	useSuggestionNativeAtis,
	useSuggestionPreferredAtis,
} from '../../../hooks';

export function MenuWrapper({ children }: { children: ReactNode }): React.JSX.Element {
	const crossAppCreationEnabled = fg('navx_universal_create_cross_app_creation_enabled');
	const { product, suggestionConfig, subProduct } = useMenuControl();

	const { locale } = useIntl();

	// Use the shared hooks for ATIs logic
	const nativeAtis = useSuggestionNativeAtis();
	const preferredAtis = useSuggestionPreferredAtis();
	const excludeAtis = useSuggestionExcludeAtis();

	const { data, fetchUCSuggestions } = useUCSuggestions({
		cloudId: suggestionConfig.enabled ? suggestionConfig.cloudId : '',
		orgId: suggestionConfig.enabled ? suggestionConfig.orgId : '',
		userId: suggestionConfig.enabled ? suggestionConfig.accountId : '',
		product,
		locale,
		subProduct: suggestionConfig.enabled ? subProduct : undefined,
		nativeAtis,
		preferredAtis,
		excludeAtis,
	});

	const noDataInStore = useMemo(() => !data, [data]);

	/**
	 * This is the last fallback call when menu is opened by no data is in store
	 */
	useEffect(
		function onMenuOpenFetch() {
			// TODO: add v2 fetch here
			if (noDataInStore && crossAppCreationEnabled && suggestionConfig.enabled) {
				fetchUCSuggestions();
			}
		},
		[crossAppCreationEnabled, suggestionConfig.enabled, locale, noDataInStore, fetchUCSuggestions],
	);

	return <ConditionalRelayEnvironmentProvider>{children}</ConditionalRelayEnvironmentProvider>;
}
